import {createContext, useContext, useEffect, useId, useRef, useState, type ReactNode} from 'react';
import {type ColumnaDeTabla, type DefinicionDeColumnas} from './columnas';
import {ordenarFilas, TABLAS_DE_CASHFLOW, type TablaDeCashflow} from './columnas-cashflow';
import {SelectorDeColumnas} from './selector-de-columnas';
import {trx} from '../i18n/idioma';
import {Tabla} from './tabla';
import {useColumnas} from './usar-columnas';

// Columnas visibles de cada tabla de Cashflow, en un solo lugar para que una
// vista guardada las cambie todas a la vez.
type Columnas = Record<TablaDeCashflow, readonly [string[], (ids: string[]) => void]>;
const ContextoDeColumnas = createContext<Columnas | null>(null);

// Tablas que se ven en la pantalla, para "⬇ Descargar lo que se ve" (Daniel,
// 15/10/2026): cada tabla se anota con sus columnas visibles y sus filas; las
// paginadas dan ademas como traer todas las filas con los filtros puestos.
export type HojaParaExcel = {
  nombre: string;
  encabezados: string[];
  filas: () => Promise<Array<Array<string | number | null>>>;
};
type Registro = Map<string, HojaParaExcel>;
const ContextoDeTablas = createContext<Registro | null>(null);

export function ColumnasDeCashflow({children}: {children: (columnas: Columnas, tablas: Registro) => ReactNode}) {
  const columnas: Columnas = {
    movimientos: useColumnas(TABLAS_DE_CASHFLOW.movimientos),
    enProceso: useColumnas(TABLAS_DE_CASHFLOW.enProceso),
    pagos: useColumnas(TABLAS_DE_CASHFLOW.pagos),
    acuerdos: useColumnas(TABLAS_DE_CASHFLOW.acuerdos),
    porEmpresa: useColumnas(TABLAS_DE_CASHFLOW.porEmpresa),
  };
  const tablas = useRef<Registro>(new Map()).current;
  return (
    <ContextoDeColumnas.Provider value={columnas}>
      <ContextoDeTablas.Provider value={tablas}>{children(columnas, tablas)}</ContextoDeTablas.Provider>
    </ContextoDeColumnas.Provider>
  );
}

// Valor de una celda para el Excel: el numero tal cual en las columnas numericas, si no el texto de la pantalla.
function valorParaExcel<T extends object>(columna: ColumnaDeTabla<T, string>, fila: T): string | number | null {
  const crudo = (fila as Record<string, unknown>)[columna.id];
  if (columna.numerica && typeof crudo === 'number') return crudo;
  const texto = columna.valor(fila);
  return texto === '—' || texto === '' ? null : texto;
}

const BOTON = 'rounded-md border border-linea bg-white px-2 py-0.5 text-xs hover:bg-papel';

// Tabla de Cashflow con el formato del Consolidado: encabezado fijo con fondo
// propio (las filas no se ven pasar por debajo), columnas que se eligen y se
// arrastran, orden por columna y la primera columna fija al moverse a los lados.
// Sin `orden` del servidor, ordena en la pantalla.
export function TablaDeCashflowComun<T extends object>({
  tabla,
  definicion,
  filas,
  claveDeFila,
  celda,
  mensajeVacio,
  altoMaximo,
  claseDeFila,
  numeracionDesde,
  orden: ordenDelServidor,
  titulo,
  hoja,
  todasLasFilas,
}: {
  tabla: TablaDeCashflow;
  definicion: DefinicionDeColumnas<T, string>;
  filas: T[];
  claveDeFila: (fila: T) => string | number;
  celda?: (columna: ColumnaDeTabla<T, string>, fila: T) => ReactNode | undefined;
  mensajeVacio: string;
  altoMaximo?: string;
  claseDeFila?: (fila: T) => string | undefined;
  numeracionDesde?: number;
  // Orden que hace el servidor (por la direccion); sin el, se ordena aqui.
  orden?: {orden: string; direccion: 'asc' | 'desc'; onOrdenar: (orden: string) => void};
  titulo?: ReactNode;
  // Nombre de la hoja al descargar lo que se ve.
  hoja?: string;
  // Tablas paginadas: todas las filas con los filtros puestos (si no, las que se ven).
  todasLasFilas?: () => Promise<T[]>;
}) {
  const columnas = useContext(ContextoDeColumnas);
  const tablas = useContext(ContextoDeTablas);
  const llave = useId();
  const [visibles, cambiar] = columnas?.[tabla] ?? [
    definicion.columnas.filter(c => c.porDefecto).map(c => c.id),
    () => {},
  ];
  const [local, setLocal] = useState<{orden: string; direccion: 'asc' | 'desc'}>({orden: '', direccion: 'asc'});
  const orden = ordenDelServidor ?? {
    ...local,
    onOrdenar: (o: string) =>
      setLocal(actual => ({orden: o, direccion: actual.orden === o && actual.direccion === 'asc' ? 'desc' : 'asc'})),
  };
  const ordenadas = ordenDelServidor ? filas : ordenarFilas(filas, local.orden, local.direccion);
  // Solo las columnas que existen en esta tabla (p. ej. sin "Acciones" para quien solo consulta).
  const ids = visibles.filter(id => definicion.columnas.some(c => c.id === id));
  // Se anota (y se borra al salir de la pantalla) para la descarga.
  useEffect(() => {
    if (!tablas) return;
    const cols = ids
      .map(id => definicion.columnas.find(c => c.id === id))
      .filter((c): c is ColumnaDeTabla<T, string> => !!c && c.id !== 'acciones');
    tablas.set(llave, {
      nombre: hoja ?? tabla,
      encabezados: cols.map(c => trx(c.titulo)),
      filas: async () => {
        const todas = todasLasFilas ? await todasLasFilas() : ordenadas;
        return todas.map(f => cols.map(c => valorParaExcel(c, f)));
      },
    });
    return () => void tablas.delete(llave);
  });
  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-0 flex-1">{titulo}</div>
        {/* Siempre a la derecha, tambien cuando el titulo es largo y baja de linea (Daniel, 15/10/2026). */}
        <div className="ml-auto">
          <SelectorDeColumnas definicion={definicion} visibles={ids} onCambiar={cambiar} claseDelBoton={BOTON} />
        </div>
      </div>
      <Tabla
        filas={ordenadas}
        definicion={definicion}
        columnasVisibles={ids}
        claveDeFila={claveDeFila}
        orden={orden.orden}
        direccion={orden.direccion}
        onOrdenar={orden.onOrdenar}
        onReordenar={cambiar}
        mensajeVacio={mensajeVacio}
        celda={celda}
        altoMaximo={altoMaximo}
        claseDeFila={claseDeFila}
        numeracionDesde={numeracionDesde}
      />
    </div>
  );
}
